/***
 * Created on 2025/4/29
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useCallback, useEffect, useMemo, useReducer, useRef} from 'react';
import {useTranslation} from "react-i18next";
import TableComponent from "../common/TableComponent/TableComponent";
import CloudSpaceOrganizationUsageRowComponent from "./CloudSpaceOrganizationUsageRowComponent";
import CloudSpaceUsageWarningPopoverComponent from "./CloudSpaceUsageWarningPopoverComponent";

function reducerHandler(state, action) {
    switch (action.type) {
        case 'select_all':
            return {...state, select_box: true};
        case 'select_none':
            return {...state, select_box: false};
        case 'select_partial':
            return {...state, select_box: 'partial'};
        case 'show_warning':
            return {...state, show_warning: action.payload};
        case 'toggle_popover':
            return {
                ...state,
                show_popover: action.payload.show_popover,
                data: action.payload.data,
            };
        default:
            return state;
    }
}

function reducerInitializer(isReadOnly) {
    return {
        select_box: false,
        show_warning: false,
        show_popover: false,
        data: null,
        isReadOnly,
    };
}

function CloudSpaceOrganizationUsage({usage_data, isReadOnly = false}) {
    const { t } = useTranslation();
    const hideTimerRef = useRef(null);

    const table_head_fields = [
        {field_name: t('@Region@'), isSortable: true, width_style: '!w-20', field_id: 'region', default_sort: true, default_sort_order: '', sortByHTMLRender: true},
        {field_name: t('@Assigned Seats@'), isSortable: false, width_style: '!w-11'},
        {field_name: t('@Total Seats@'), isSortable: true, width_style: '!w-11'},
        {field_name: t('@Usage@'), isSortable: false, width_style: '!w-26'},
        {field_name: t('@Date Created@'), isSortable: false, width_style: '!w-16'},
        {field_name: t('@Action@'), isSortable: false, width_style: '!w-16'},
    ];

    const [state, dispatch] = useReducer(reducerHandler, isReadOnly, reducerInitializer);

    const scheduleHide = useCallback((delay = 0) => {
        clearTimeout(hideTimerRef.current);
        if (delay > 0) {
            hideTimerRef.current = setTimeout(() => {
                dispatch({type: 'toggle_popover', payload: {show_popover: false, data: null}});
            }, delay);
        } else {
            dispatch({type: 'toggle_popover', payload: {show_popover: false, data: null}});
        }
    }, []);

    const cancelHide = useCallback(() => {
        clearTimeout(hideTimerRef.current);
    }, []);

    useEffect(() => {
        return () => clearTimeout(hideTimerRef.current);
    }, []);

    const enhancedState = useMemo(() => ({
        ...state,
        scheduleHide,
        cancelHide,
    }), [state, scheduleHide, cancelHide]);

    return (
        <div className="content-grid__row content-grid__row--usage-table">
            <div className="section-title-bar">
                <div className="col-label">{t('@Organization Usage@')}</div>
            </div>
            <div className="section-content indent-l-15">
                <TableComponent
                    table_head_fields={table_head_fields}
                    table_data={usage_data}
                    disPatcher={dispatch}
                    search={false}
                    row_component={CloudSpaceOrganizationUsageRowComponent}
                    css_with_context_wrapper={false}
                    data_table_name='organization-usage-table'
                    pagination={false}
                    table_ui={{table_field_checkbox: false}}
                    table_height={'335px'}
                    state={enhancedState}
                />
            </div>

            <CloudSpaceUsageWarningPopoverComponent
                state={state}
                scheduleHide={scheduleHide}
                cancelHide={cancelHide}
            />
        </div>
    );
}

export default CloudSpaceOrganizationUsage;
